/**
 * Полный список схем и таблиц для автодополнения в Monaco Editor
 * Обновляется на основе SQL миграций и YAML файлов доменов
 */

export interface SchemaInfo {
  name: string;
  description?: string;
  tables: string[];
}

export interface TableInfo {
  schema: string;
  name: string;
  description?: string;
}

/**
 * Список всех схем базы данных
 */
export const SCHEMAS: SchemaInfo[] = [
  {
    name: "logistics",
    description: "База данных для домена Logistics & Supply Chain",
    tables: [
      // RAW LAYER
      "raw_orders",
      "raw_shipments",
      "raw_inventory",
      "raw_transport",
      // STAGE LAYER
      "stage_orders",
      "stage_shipments",
      "stage_inventory_snapshots",
      "stage_product_prices",
      "stage_customer_segments",
      "stage_regions_source",
      "stage_warehouses_source",
      "stage_suppliers_source",
      "stage_products_source",
      // MART LAYER
      "mart_order_items_nested",
      "mart_shipment_metrics_nested",
      "mart_shipments_agg",
      "mart_inventory_agg",
      "mart_orders_2024",
      "mart_orders_2023",
      "mart_orders_2022",
      "mart_orders_merge",
    ],
  },
  {
    name: "default",
    description: "Схема по умолчанию",
    tables: [
      // HR Domain
      "employees",
      // E-commerce Domain
      "clients",
      "orders",
      "order_items",
      "products",
    ],
  },
];

/**
 * Плоский список всех таблиц с их схемами
 */
export const ALL_TABLES: TableInfo[] = [
  // logistics schema
  { schema: "logistics", name: "raw_orders", description: "Сырые данные заказов. Демонстрация: MergeTree, JSON, Array колонки" },
  { schema: "logistics", name: "raw_shipments", description: "Сырые данные поставок. Демонстрация: MergeTree, JSON, Array колонки" },
  { schema: "logistics", name: "raw_inventory", description: "Сырые данные инвентаря. Демонстрация: MergeTree, JSON, Array колонки, временные ряды" },
  { schema: "logistics", name: "raw_transport", description: "Сырые данные транспортировки. Демонстрация: MergeTree, JSON колонки" },
  { schema: "logistics", name: "stage_orders", description: "Очищенные данные заказов. Демонстрация: ReplacingMergeTree, дедупликация, TTL" },
  { schema: "logistics", name: "stage_shipments", description: "Очищенные данные поставок. Демонстрация: CollapsingMergeTree для изменяемых данных" },
  { schema: "logistics", name: "stage_inventory_snapshots", description: "Снимки инвентаря для ASOF JOIN. Демонстрация: временные ряды, ASOF JOIN" },
  { schema: "logistics", name: "stage_product_prices", description: "Цены товаров. Демонстрация: ANY JOIN (дубликаты ключей product_id)" },
  { schema: "logistics", name: "stage_customer_segments", description: "Сегменты клиентов. Демонстрация: LEFT JOIN, RIGHT JOIN (не все клиенты имеют сегмент)" },
  { schema: "logistics", name: "stage_regions_source", description: "Источник данных для иерархического словаря регионов. Структура: Страна -> Регион -> Город" },
  { schema: "logistics", name: "stage_warehouses_source", description: "Справочник складов" },
  { schema: "logistics", name: "stage_suppliers_source", description: "Справочник поставщиков" },
  { schema: "logistics", name: "stage_products_source", description: "Справочник товаров" },
  { schema: "logistics", name: "mart_order_items_nested", description: "Витрина заказов с Nested структурами. Демонстрация: SummingMergeTree, sumMap, Nested колонки" },
  { schema: "logistics", name: "mart_shipment_metrics_nested", description: "Витрина поставок с Nested структурами. Демонстрация: SummingMergeTree, sumMap" },
  { schema: "logistics", name: "mart_shipments_agg", description: "Агрегированная витрина поставок. Демонстрация: SummingMergeTree автосуммирование" },
  { schema: "logistics", name: "mart_inventory_agg", description: "Агрегированная витрина инвентаря. Демонстрация: AggregatingMergeTree, SimpleAggregateFunction" },
  { schema: "logistics", name: "mart_orders_2024", description: "Заказы за 2024 год. Используется в Merge таблице" },
  { schema: "logistics", name: "mart_orders_2023", description: "Заказы за 2023 год. Используется в Merge таблице" },
  { schema: "logistics", name: "mart_orders_2022", description: "Заказы за 2022 год. Используется в Merge таблице" },
  { schema: "logistics", name: "mart_orders_merge", description: "Merge таблица для объединения заказов по годам. Демонстрация: Merge Engine, объединение нескольких таблиц" },
  // default schema
  { schema: "default", name: "employees", description: "Таблица сотрудников компании" },
  { schema: "default", name: "clients", description: "Таблица с информацией о клиентах интернет-магазина" },
  { schema: "default", name: "orders", description: "Таблица заказов клиентов" },
  { schema: "default", name: "order_items", description: "Таблица позиций в заказах" },
  { schema: "default", name: "products", description: "Справочник товаров интернет-магазина" },
];

/**
 * Получить все схемы
 */
export function getAllSchemas(): string[] {
  return SCHEMAS.map(s => s.name);
}

/**
 * Получить все таблицы для указанной схемы
 */
export function getTablesBySchema(schemaName: string): string[] {
  const schema = SCHEMAS.find(s => s.name.toLowerCase() === schemaName.toLowerCase());
  return schema?.tables || [];
}

/**
 * Получить информацию о таблице
 */
export function getTableInfo(schemaName: string, tableName: string): TableInfo | undefined {
  return ALL_TABLES.find(
    t => t.schema.toLowerCase() === schemaName.toLowerCase() &&
         t.name.toLowerCase() === tableName.toLowerCase()
  );
}

/**
 * Получить все таблицы (схема.таблица)
 */
export function getAllTablesWithSchema(): string[] {
  return ALL_TABLES.map(t => `${t.schema}.${t.name}`);
}

/**
 * Получить все таблицы без схемы
 */
export function getAllTableNames(): string[] {
  return ALL_TABLES.map(t => t.name);
}



